import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  VStack,
  Text,
  Badge,
  Divider,
  Flex,
  Avatar,
  useToast,
  Input,
  IconButton,
  Image,
  useBreakpointValue,
  Show,
  Hide
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowBackIcon, ChatIcon } from '@chakra-ui/icons';
import { sendUserMessage, fetchUserInbox, fetchSentMessages } from '../../Redux/Login/action'; // Import required actions

const ConversationModal = ({ isOpen, onClose, conversation }) => {  const dispatch = useDispatch();
  const toast = useToast();
  const [replyText, setReplyText] = useState('');
  const sendingMessage = useSelector(state => state.auth.messages?.sendingMessage) || false;
  const currentUserAvatar = useSelector(state => state.auth.profile?.userAvatar) || "";
  const defaultAvatarUrl = "https://via.placeholder.com/40";
  
  // Format time function
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };  // Simple helper function to get valid avatar URL
  const getValidAvatarUrl = (url) => {
    if (!url || url === "") return defaultAvatarUrl;
    if (url.includes('bunny-burrow-pull-1.b-cdn.net')) return defaultAvatarUrl;
    return url;
  };

  // Log avatar sources for debugging on component mount
  useEffect(() => {
    if (conversation) {
      console.log("Conversation partner avatar:", conversation.partnerAvatar);
      
      // Check avatar URL and log the avatar source that will be used
      const avatarToUse = getValidAvatarUrl(conversation.partnerAvatar);
      console.log("Using avatar URL:", avatarToUse);
      
      if (conversation.messages && conversation.messages.length > 0) {
        const partnerMessages = conversation.messages.filter(msg => msg.fromBurrowerId !== 'me');
        if (partnerMessages.length > 0) {
          console.log("Partner message avatar samples:", 
            partnerMessages.slice(0, 2).map(msg => ({
              fromId: msg.fromBurrowerId,
              userAvatar: msg.userAvatar,
              senderAvatar: msg.senderAvatar
            }))
          );
        }
      }
    }
  }, [conversation]);

  // Reset reply text when modal closes
  useEffect(() => {
    if (!isOpen) setReplyText('');
  }, [isOpen]);
  // Handle sending a reply
  const handleSendReply = () => {
    if (!replyText.trim()) return;
    
    // Store message content before clearing it
    const messageContent = replyText.trim();
      // Dispatch action to send the message - pass as separate parameters, not as an object
    dispatch(sendUserMessage(conversation?.burrowerId, messageContent)).then(result => {
      if (result.success) {
        toast({
          title: "Reply sent",
          status: "success",
          duration: 3000,
        });
        
        // Immediately add the sent message to the conversation for instant feedback
        if (conversation && conversation.messages) {
          const newMessage = {
            fromBurrowerId: 'me',
            messageContents: messageContent,
            sentAt: new Date().toISOString(),
            sentMessageId: `temp-${Date.now()}`, // Temporary ID until refresh
          };
          
          conversation.messages.push(newMessage);
        }
        
        // Reset input
        setReplyText('');
        
        // Refresh inbox to get the updated conversation with proper IDs
        dispatch(fetchUserInbox());
        dispatch(fetchSentMessages());
      } else {
        toast({
          title: "Failed to send reply",
          description: result.message || "Please try again",
          status: "error",
          duration: 3000,
        });
      }
    });
  };

  if (!conversation) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "lg" }} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent 
        maxH={{ base: "100vh", md: "80vh" }} 
        my={{ base: 0, md: "3.75rem" }}
        borderRadius={{ base: 0, md: "md" }}
        bg="gray.800" 
        color="white"
      >
        <ModalHeader pb={2} pr={10}>
          <Flex align="center" gap={2}>
            <IconButton
              icon={<ArrowBackIcon />}
              aria-label="Go back"
              variant="ghost"
              size="sm"
              onClick={onClose}
            />
            <Hide below="md">
              <Avatar 
                size="sm" 
                name={conversation.burrowerId}
                src={conversation.partnerAvatar || defaultAvatarUrl}
              />
            </Hide>
            <Text fontSize={{ base: "md", md: "lg" }} fontWeight="bold" isTruncated>
              Conversation with {conversation.burrowerId}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {conversation.messages?.length === 0 ? (
              <Box textAlign="center" py={10}>
                <ChatIcon boxSize="40px" color="gray.400" mb={3} />
                <Text>No messages in this conversation</Text>
              </Box>
            ) : (
              conversation.messages?.map((message) => {
                const isFromSelf = message.fromBurrowerId === 'me';
                
                // Determine best avatar source for partner
                let partnerAvatar = conversation.partnerAvatar;
                if (!partnerAvatar && message.fromBurrowerId !== 'me') {
                  // Try to get from message properties that might contain the avatar
                  partnerAvatar = message.userAvatar || message.senderAvatar || null;
                }
                
                return (
                  <Box 
                    key={message.userMessageId || message.sentMessageId} 
                    alignSelf={isFromSelf ? "flex-end" : "flex-start"}
                    maxW="80%"
                  >                    
                    <Flex 
                      direction={isFromSelf ? "row-reverse" : "row"} 
                      align="flex-start"
                    >                      <Avatar 
                        size="sm" 
                        name={isFromSelf ? "Me" : message.fromBurrowerId}
                        src={isFromSelf 
                          ? currentUserAvatar || defaultAvatarUrl 
                          : partnerAvatar || defaultAvatarUrl}
                        ml={isFromSelf ? 2 : 0}
                        mr={isFromSelf ? 0 : 2}
                      />
                      <Box>
                        <Flex 
                          justify={isFromSelf ? "flex-end" : "flex-start"}
                          mb={1}
                        >
                          <Text fontSize="xs" color="gray.500">
                            {formatTime(message.sentAt)}
                          </Text>
                        </Flex>
                        <Box 
                          bg={isFromSelf ? "blue.900" : "gray.700"} 
                          p={3} 
                          borderRadius="lg" 
                          borderTopRightRadius={isFromSelf ? "sm" : "lg"}
                          borderTopLeftRadius={isFromSelf ? "lg" : "sm"}
                        >
                          <Text color="white">
                            {message.userMessageContents || message.messageContents}
                          </Text>
                        </Box>
                      </Box>
                    </Flex>
                  </Box>
                );
              })
            )}
          </VStack>
        </ModalBody>

        <ModalFooter p={{ base: 2, md: 4 }}>
          <Flex width="100%" gap={2}>
            <Input
              placeholder="Type a message..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              size={{ base: "sm", md: "md" }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
            />
            <Button 
              colorScheme="blue" 
              onClick={handleSendReply}
              isLoading={sendingMessage}
              loadingText="Send"
              isDisabled={!replyText.trim()}
              size={{ base: "sm", md: "md" }}
            >
              Send
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConversationModal;